// The block document, its kinds and its edit operations (design h4 §B.3, §B.4; plan row H.11).
// One zod schema per block kind, the document a configurator reads (`readBlocks`) and the
// operations it sends back (`applyBlockOps`), with a JSON Schema export for clients not written
// in TypeScript. Constraints come from the contracts constants every other reader uses and are
// not restated here; `BLOCKS_VERSION` moves only when a field is removed or retyped (§B.2).
// This module loads no `yaml`: the wire (`api/projects.ts`, on the root index) reads the document
// and operation schemas from here, while the codec that needs `yaml` stays on the `./blocks` subpath.
import { z } from "zod";
import { MCP_SERVER_NAME, MCP_SERVER_URL_MAX, MCP_SERVERS_MAX, mcpServerUrlIssue, } from "../agent-mcp.js";
import { CatalogAgentSchema, CatalogOutcomeSchema, RUNNER_PINS } from "../catalog-sidecar.js";
import { MEMORY_SCOPE_SLUG, MEMORY_SCOPES_PER_SESSION_MAX } from "../memory.js";
import { ModelIdSchema, modelRefIssue } from "../models.js";
import { reservedToolNameIssue, TemplateRefSchema } from "../multiagent.js";
import { ProjectSidecarSchema, TemplateVersionRefSchema } from "../projects.js";
import { RUNTIME_PROVIDERS } from "../runtime.js";
import { BUILTIN_TOOL_NAMES } from "../wire.js";
import { TOOL_POLICIES } from "./policies.js";
export const BLOCKS_VERSION = 1;
/** How many operations one `projects.applyBlocks` call takes (design h4 §B.5). */
export const BLOCK_OPS_MAX = 100;
export const BLOCK_KINDS = [
    "identity",
    "instructions",
    "goal",
    "model",
    "tools",
    "mcp",
    "skills",
    "custom_tools",
    "outcome",
    "team",
    "runtime",
];
export const BlockKindSchema = z.enum(BLOCK_KINDS);
/** Where a block's fields live: which of the revision's documents, and where in it (a pointer, `""` for the whole document). */
export const BlockSourceSchema = z.object({
    document: z.enum(["agent", "overlay", "sidecar"]),
    pointer: z.string(),
});
/** Why a block cannot be edited: it belongs to a member, its work is held, or the codec does not model its shape. */
export const READ_ONLY_REASONS = ["member", "held", "unmodelled_shape"];
export const ReadOnlyReasonSchema = z.enum(READ_ONLY_REASONS);
const blockBase = {
    source: BlockSourceSchema,
    editable: z.boolean(),
    readOnlyReason: ReadOnlyReasonSchema.optional(),
};
/* ---------------- field shapes, shared by the blocks and the `set` operations ---------------- */
export const IdentityValueSchema = z.object({
    name: CatalogAgentSchema.shape.name,
    description: CatalogAgentSchema.shape.description,
});
export const InstructionsValueSchema = z.object({ system: CatalogAgentSchema.shape.system });
export const GoalValueSchema = z.object({ goal: ProjectSidecarSchema.shape.goal });
/**
 * A model id as the draft gate takes it (`CatalogAgentSchema`, `modelRefIssue`): a catalog id, a
 * provider-prefixed id, or a bare name the catalog does not know (a provider's native name such
 * as `gpt-5.4-mini`, a deployment). The model BLOCK reads the document's spelling through this,
 * so a read never refuses what a save accepted; the `set model` OPERATION keeps `ModelIdSchema`
 * (canonical spelling), the rule a configurator writes by.
 */
export const AuthoredModelIdSchema = z
    .string()
    .min(1)
    .refine((id) => modelRefIssue(id) === null, {
    error: (issue) => modelRefIssue(String(issue.input)) ?? "not a model id",
});
/** The model as an operation writes it: a canonical ref string, or the CMA model object's extra fields beside its id. */
export const ModelValueSchema = z.object({
    ref: ModelIdSchema,
    effort: z.union([z.string(), z.number()]).optional(),
    speed: z.string().optional(),
    inferenceGeo: z.string().optional(),
});
export const ToolPolicySchema = z.enum(TOOL_POLICIES);
export const BuiltinToolNameSchema = z.enum(BUILTIN_TOOL_NAMES);
export const BuiltinToolStateSchema = z.object({
    name: BuiltinToolNameSchema,
    enabled: z.boolean(),
    policy: ToolPolicySchema,
    explicit: z.boolean(),
});
export const McpServerNameSchema = z
    .string()
    .regex(MCP_SERVER_NAME, "an MCP server name: letters, digits, _ and -, up to 64");
export const McpServerUrlSchema = z
    .string()
    .max(MCP_SERVER_URL_MAX)
    .refine((url) => mcpServerUrlIssue(url) === null, {
    error: (issue) => mcpServerUrlIssue(String(issue.input))?.message ?? "not an MCP server URL",
});
export const McpServerStateSchema = z.object({
    name: McpServerNameSchema,
    /** The URL's host; the full `url` rides only in the owner's own read. */
    host: z.string(),
    url: z.string().optional(),
    enabled: z.boolean(),
    policy: ToolPolicySchema,
    /** The tool names an allowlist toolset (`default_config.enabled: false` plus per-tool `enabled: true`) exposes. */
    allowlist: z.array(z.string()).optional(),
});
export const SkillSchema = z.object({
    type: z.enum(["anthropic", "custom"]),
    id: z.string().min(1),
    version: z.string().optional(),
});
export const CustomToolValueSchema = z.object({
    name: z
        .string()
        .min(1)
        .refine((name) => reservedToolNameIssue(name) === null, {
        error: (issue) => reservedToolNameIssue(String(issue.input)) ?? "not a tool name",
    }),
    description: z.string().optional(),
    inputSchema: z.record(z.string(), z.unknown()).optional(),
});
export const TEAM_VARIANTS = ["multiagent_20261001", "coordinator"];
export const TeamVariantSchema = z.enum(TEAM_VARIANTS);
/** The list a member sits in: the coordinator's `agents`, or a 20261001 block's `subagents` or `workflows` roster. */
export const TEAM_LISTS = ["agents", "subagents", "workflows"];
export const TeamListSchema = z.enum(TEAM_LISTS);
export const TeamMemberSchema = z.object({ ref: TemplateRefSchema, list: TeamListSchema });
export const TeamListSettingSchema = z.object({ enabled: z.boolean(), inline: z.boolean() });
export const TeamAdvisorSchema = z.object({ model: ModelIdSchema });
export const RuntimeSeatSchema = z.object({
    authoredModel: z.string().min(1),
    provider: z.enum(RUNTIME_PROVIDERS),
    seat: z.string().min(1),
});
export const RuntimeValueSchema = z.object({
    runner: z.enum(RUNNER_PINS).nullable().optional(),
    memoryScopes: z
        .array(z.string().regex(MEMORY_SCOPE_SLUG, "a scope slug: a-z, 0-9 and -, up to 63"))
        .min(1)
        .max(MEMORY_SCOPES_PER_SESSION_MAX)
        .nullable()
        .optional(),
});
/* ---------------- the block kinds ---------------- */
export const IdentityBlockSchema = z.object({
    kind: z.literal("identity"),
    ...blockBase,
    ...IdentityValueSchema.shape,
});
export const InstructionsBlockSchema = z.object({
    kind: z.literal("instructions"),
    ...blockBase,
    ...InstructionsValueSchema.shape,
});
export const GoalBlockSchema = z.object({
    kind: z.literal("goal"),
    ...blockBase,
    ...GoalValueSchema.shape,
});
export const ModelBlockSchema = z.object({
    kind: z.literal("model"),
    ...blockBase,
    ...ModelValueSchema.shape,
    /** As the document spells it (the gate's rule), never narrower than what was saved. */
    ref: AuthoredModelIdSchema,
});
export const ToolsBlockSchema = z.object({
    kind: z.literal("tools"),
    ...blockBase,
    toolset: z.enum(["present", "absent"]),
    defaultEnabled: z.boolean(),
    defaultPolicy: ToolPolicySchema,
    builtins: z.array(BuiltinToolStateSchema),
});
export const McpBlockSchema = z.object({
    kind: z.literal("mcp"),
    ...blockBase,
    servers: z.array(McpServerStateSchema).max(MCP_SERVERS_MAX),
});
export const SkillsBlockSchema = z.object({
    kind: z.literal("skills"),
    ...blockBase,
    skills: z.array(SkillSchema),
});
export const CustomToolsBlockSchema = z.object({
    kind: z.literal("custom_tools"),
    ...blockBase,
    tools: z.array(CustomToolValueSchema),
});
export const OutcomeBlockSchema = z.object({
    kind: z.literal("outcome"),
    ...blockBase,
    outcome: CatalogOutcomeSchema.optional(),
});
export const TeamBlockSchema = z.object({
    kind: z.literal("team"),
    ...blockBase,
    variant: TeamVariantSchema,
    members: z.array(TeamMemberSchema),
    self: z.boolean(),
    advisor: TeamAdvisorSchema.nullable(),
    subagents: TeamListSettingSchema,
    workflows: TeamListSettingSchema,
});
export const RuntimeBlockSchema = z.object({
    kind: z.literal("runtime"),
    ...blockBase,
    runner: z.enum(RUNNER_PINS).optional(),
    memoryScopes: z.array(z.string()).optional(),
    seats: z.array(RuntimeSeatSchema),
});
export const BlockSchema = z.discriminatedUnion("kind", [
    IdentityBlockSchema,
    InstructionsBlockSchema,
    GoalBlockSchema,
    ModelBlockSchema,
    ToolsBlockSchema,
    McpBlockSchema,
    SkillsBlockSchema,
    CustomToolsBlockSchema,
    OutcomeBlockSchema,
    TeamBlockSchema,
    RuntimeBlockSchema,
]);
/* ---------------- the document ---------------- */
/** The agent an op addresses or a finding names: the project's root agent.yaml, or a member by its roster id. */
export const BlockAgentKeySchema = z.union([z.literal("root"), TemplateRefSchema]);
export const BlockAgentSchema = z.object({
    key: BlockAgentKeySchema,
    name: z.string(),
    role: z.enum(["root", "member"]),
    /** A member's pinned template version (a template project pins its members at save). */
    pinned: TemplateVersionRefSchema.optional(),
    blocks: z.array(BlockSchema),
});
export const BlockDocumentSchema = z.object({
    blocksVersion: z.literal(BLOCKS_VERSION),
    projectId: z.string().uuid(),
    revision: z.number().int().positive(),
    /** The root agent first, then every member at the version the project pins, read-only. */
    agents: z.array(BlockAgentSchema).min(1),
    project: z.object({
        goal: GoalBlockSchema,
        outcome: OutcomeBlockSchema,
        runtime: RuntimeBlockSchema,
    }),
    /** Top-level keys no block models, kept exactly as written: the root agent.yaml's and the overlay's. */
    unmodelled: z.object({ agentKeys: z.array(z.string()), overlayKeys: z.array(z.string()) }),
    resources: z.object({
        uploads: z.number().int().nonnegative(),
        memoryScopes: z.number().int().nonnegative(),
    }),
});
/* ---------------- the edit operations ---------------- */
/** The agent an operation addresses; the root when absent. An operation on a member is refused (`member_read_only`). */
const target = { agent: BlockAgentKeySchema.optional() };
export const SetIdentityOpSchema = z.object({
    op: z.literal("set"),
    kind: z.literal("identity"),
    ...target,
    value: IdentityValueSchema,
});
export const SetInstructionsOpSchema = z.object({
    op: z.literal("set"),
    kind: z.literal("instructions"),
    ...target,
    value: InstructionsValueSchema,
});
export const SetGoalOpSchema = z.object({
    op: z.literal("set"),
    kind: z.literal("goal"),
    ...target,
    /** `goal: null` removes the goal. */
    value: z.object({ goal: ProjectSidecarSchema.shape.goal.unwrap().nullable() }),
});
export const SetModelOpSchema = z.object({
    op: z.literal("set"),
    kind: z.literal("model"),
    ...target,
    value: ModelValueSchema,
});
export const SetOutcomeOpSchema = z.object({
    op: z.literal("set"),
    kind: z.literal("outcome"),
    ...target,
    /** `outcome: null` removes the outcome. */
    value: z.object({ outcome: CatalogOutcomeSchema.nullable() }),
});
export const SetRuntimeOpSchema = z.object({
    op: z.literal("set"),
    kind: z.literal("runtime"),
    ...target,
    value: RuntimeValueSchema,
});
/** `set` replaces one block's fields; the value's shape follows the kind. */
export const SetOpSchema = z.discriminatedUnion("kind", [
    SetIdentityOpSchema,
    SetInstructionsOpSchema,
    SetGoalOpSchema,
    SetModelOpSchema,
    SetOutcomeOpSchema,
    SetRuntimeOpSchema,
]);
export const SetToolOpSchema = z.object({
    op: z.literal("setTool"),
    ...target,
    name: BuiltinToolNameSchema,
    enabled: z.boolean().optional(),
    policy: ToolPolicySchema.optional(),
});
export const AddMcpServerOpSchema = z.object({
    op: z.literal("addMcpServer"),
    ...target,
    name: McpServerNameSchema,
    url: McpServerUrlSchema,
    /** On, asking, when left out: CMA's documented default for an MCP toolset. */
    enabled: z.boolean().optional(),
    policy: ToolPolicySchema.optional(),
});
export const SetMcpServerOpSchema = z.object({
    op: z.literal("setMcpServer"),
    ...target,
    name: McpServerNameSchema,
    url: McpServerUrlSchema.optional(),
    enabled: z.boolean().optional(),
    policy: ToolPolicySchema.optional(),
});
export const RemoveMcpServerOpSchema = z.object({
    op: z.literal("removeMcpServer"),
    ...target,
    name: McpServerNameSchema,
});
export const AddSkillOpSchema = z.object({
    op: z.literal("addSkill"),
    ...target,
    skill: SkillSchema,
});
export const RemoveSkillOpSchema = z.object({
    op: z.literal("removeSkill"),
    ...target,
    id: z.string().min(1),
});
export const AddCustomToolOpSchema = z.object({
    op: z.literal("addCustomTool"),
    ...target,
    tool: CustomToolValueSchema,
});
export const SetCustomToolOpSchema = z.object({
    op: z.literal("setCustomTool"),
    ...target,
    tool: CustomToolValueSchema,
});
export const RemoveCustomToolOpSchema = z.object({
    op: z.literal("removeCustomTool"),
    ...target,
    name: z.string().min(1),
});
export const SetTeamOpSchema = z.object({
    op: z.literal("setTeam"),
    ...target,
    /** `team: null` removes the block; a new block is `multiagent_20261001` unless the variant is named. */
    team: z.object({ variant: TeamVariantSchema.optional() }).nullable(),
});
export const AddMemberOpSchema = z.object({
    op: z.literal("addMember"),
    ...target,
    ref: TemplateRefSchema,
    list: TeamListSchema.optional(),
});
export const RemoveMemberOpSchema = z.object({
    op: z.literal("removeMember"),
    ...target,
    ref: TemplateRefSchema,
});
export const SetAdvisorOpSchema = z.object({
    op: z.literal("setAdvisor"),
    ...target,
    /** `advisor: null` disables the advisor. */
    advisor: TeamAdvisorSchema.nullable(),
});
export const SetListOpSchema = z.object({
    op: z.literal("setList"),
    ...target,
    list: z.enum(["subagents", "workflows"]),
    enabled: z.boolean().optional(),
    inline: z.boolean().optional(),
});
export const SetSeatOpSchema = z.object({
    op: z.literal("setSeat"),
    ...target,
    authoredModel: RuntimeSeatSchema.shape.authoredModel,
    provider: RuntimeSeatSchema.shape.provider,
    /** `seat: null` removes the seat. */
    seat: RuntimeSeatSchema.shape.seat.nullable(),
});
export const BlockOpSchema = z.discriminatedUnion("op", [
    SetOpSchema,
    SetToolOpSchema,
    AddMcpServerOpSchema,
    SetMcpServerOpSchema,
    RemoveMcpServerOpSchema,
    AddSkillOpSchema,
    RemoveSkillOpSchema,
    AddCustomToolOpSchema,
    SetCustomToolOpSchema,
    RemoveCustomToolOpSchema,
    SetTeamOpSchema,
    AddMemberOpSchema,
    RemoveMemberOpSchema,
    SetAdvisorOpSchema,
    SetListOpSchema,
    SetSeatOpSchema,
]);
/**
 * Why an operation was refused: it addresses a member; it names a block or an entry the
 * document does not have; its value fails the kind's constraints; it would pass a count limit;
 * the work behind it is held (Foundry, decision 24); or the codec does not model the shape it
 * edits yet.
 */
export const BLOCK_OP_REASONS = [
    "member_read_only",
    "unknown_block",
    "invalid_value",
    "limit_exceeded",
    "held",
    "unmodelled_shape",
];
export const BlockOpReasonSchema = z.enum(BLOCK_OP_REASONS);
export const BlockOpErrorSchema = z.object({
    index: z.number().int().nonnegative(),
    reason: BlockOpReasonSchema,
});
/* ---------------- JSON Schema ---------------- */
const JSON_SCHEMA_OPTIONS = { io: "input", target: "draft-7" };
const definition = (schema) => {
    const { $schema: _, ...rest } = z.toJSONSchema(schema, JSON_SCHEMA_OPTIONS);
    return rest;
};
let blocksJsonSchemaCache;
/**
 * The block contract as JSON Schema (draft-07), for configurators not written in TypeScript:
 * the contracts build writes it to `dist/blocks.schema.json`. `definitions.BlockDocument` is
 * what `readBlocks` returns, `definitions.BlockOp` one edit operation, `definitions.BlockOpError`
 * a refusal.
 */
export function blocksJsonSchema() {
    blocksJsonSchemaCache ??= {
        $schema: "http://json-schema.org/draft-07/schema#",
        title: "@mfa/v5-contracts/blocks",
        description: "The block document a configurator reads, the edit operations it sends, and a refused operation.",
        blocksVersion: BLOCKS_VERSION,
        definitions: {
            BlockDocument: definition(BlockDocumentSchema),
            BlockOp: definition(BlockOpSchema),
            BlockOpError: definition(BlockOpErrorSchema),
        },
    };
    return blocksJsonSchemaCache;
}
//# sourceMappingURL=schema.js.map